import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { MintedToken } from '../lib/tokens';
import { Button } from './ui/Button';

// Reveal-once notice (#835): the plaintext `dev_pat_…` a mint just returned.
// The API stores only its sha256 hash, so this is the ONE moment it exists —
// dismissing the notice drops it for good, and the row underneath keeps nothing
// but a prefix. Hence: copy button first, dismissal explicit (never a timed
// toast that could expire while its owner is switching windows).

export type MintedTokenNoticeProps = {
  readonly minted: MintedToken;
  readonly onDismiss: () => void;
};

export function MintedTokenNotice(props: MintedTokenNoticeProps): JSX.Element {
  const [copied, setCopied] = createSignal(false);
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copy = (): void =>
    void navigator.clipboard?.writeText(props.minted.token).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  return (
    <div class="alert token-secret" role="status">
      <p class="alert__title">{t('account_tokens.minted_title')}</p>
      <p class="alert__desc">{t('account_tokens.minted_desc')}</p>
      <code class="code-block token-secret__value">{props.minted.token}</code>
      <div class="token-secret__actions">
        <Button variant="outline" size="sm" onClick={copy}>
          <IconCopy size="0.875rem" />
          {t(copied() ? 'account_tokens.copied' : 'account_tokens.copy')}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onDismiss}>
          {t('account_tokens.dismiss')}
        </Button>
      </div>
    </div>
  );
}
